import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomerDocument, DocumentUpload } from '@/types/customer-document';

// Mock data for demonstration
const mockDocuments: Record<string, CustomerDocument[]> = {
  '1': [
    {
      id: 'doc-1',
      customerId: '1',
      fileName: 'Credit_Application_2023.pdf',
      fileType: 'application/pdf',
      fileSize: 245678,
      fileUrl: '#',
      uploadDate: '2024-01-15T10:30:00Z',
      uploadedBy: '1',
      uploadedByName: 'Sarah Johnson',
      description: 'Initial credit application form',
      category: 'credit_application',
    },
    {
      id: 'doc-2',
      customerId: '1',
      fileName: 'Service_Contract_2024.docx',
      fileType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      fileSize: 156789,
      fileUrl: '#',
      uploadDate: '2024-02-20T14:45:00Z',
      uploadedBy: '2',
      uploadedByName: 'Michael Chen',
      description: 'Annual service agreement',
      category: 'contract',
    },
    {
      id: 'doc-3',
      customerId: '1',
      fileName: 'Payment_Terms_Agreement.pdf',
      fileType: 'application/pdf',
      fileSize: 98765,
      fileUrl: '#',
      uploadDate: '2024-03-10T09:15:00Z',
      uploadedBy: '1',
      uploadedByName: 'Sarah Johnson',
      description: 'Updated payment terms',
      category: 'contract',
    },
    {
      id: 'doc-4',
      customerId: '1',
      fileName: 'Account_Statement_Q1.xlsx',
      fileType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      fileSize: 234567,
      fileUrl: '#',
      uploadDate: '2024-04-05T16:20:00Z',
      uploadedBy: '3',
      uploadedByName: 'Emily Rodriguez',
      description: 'Q1 2024 account statement',
      category: 'statement',
    },
    {
      id: 'doc-5',
      customerId: '1',
      fileName: 'Correspondence_March_2024.pdf',
      fileType: 'application/pdf',
      fileSize: 123456,
      fileUrl: '#',
      uploadDate: '2024-03-28T11:30:00Z',
      uploadedBy: '1',
      uploadedByName: 'Sarah Johnson',
      description: 'Email correspondence regarding payment delay',
      category: 'correspondence',
    },
  ],
};

// Simulate API delay
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Fetch documents for a customer
const fetchCustomerDocuments = async (customerId: string): Promise<CustomerDocument[]> => {
  await delay(500);
  return mockDocuments[customerId] || [];
};

// Upload a document
const uploadDocument = async (
  customerId: string,
  upload: DocumentUpload,
  userId: string,
  userName: string,
): Promise<CustomerDocument> => {
  await delay(1000);

  const newDocument: CustomerDocument = {
    id: `doc-${Date.now()}`,
    customerId,
    fileName: upload.file.name,
    fileType: upload.file.type,
    fileSize: upload.file.size,
    fileUrl: '#', // In a real app, this would be the uploaded file URL
    uploadDate: new Date().toISOString(),
    uploadedBy: userId,
    uploadedByName: userName,
    description: upload.description,
    category: upload.category,
  };

  // Add to mock data
  if (!mockDocuments[customerId]) {
    mockDocuments[customerId] = [];
  }
  mockDocuments[customerId].unshift(newDocument);

  return newDocument;
};

// Delete a document
const deleteDocument = async (customerId: string, documentId: string): Promise<void> => {
  await delay(500);

  if (mockDocuments[customerId]) {
    mockDocuments[customerId] = mockDocuments[customerId].filter((doc) => doc.id !== documentId);
  }
};

// Hook to fetch customer documents
export function useCustomerDocuments(customerId: string | undefined) {
  return useQuery({
    queryKey: ['customerDocuments', customerId],
    queryFn: () => fetchCustomerDocuments(customerId!),
    enabled: !!customerId,
  });
}

// Hook to upload a document
export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      upload,
      userId,
      userName,
    }: {
      customerId: string;
      upload: DocumentUpload;
      userId: string;
      userName: string;
    }) => uploadDocument(customerId, upload, userId, userName),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customerDocuments', variables.customerId] });
    },
  });
}

// Hook to delete a document
export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, documentId }: { customerId: string; documentId: string }) =>
      deleteDocument(customerId, documentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customerDocuments', variables.customerId] });
    },
  });
}
